import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Autocomplete,
    Chip,
    Button,
    Card,
    CardContent,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment
} from '@mui/material';
import {
    LiveTv as LiveTvIcon,
    Search as SearchIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as TrendingUpIcon,
    CheckCircle as CheckIcon,
    Info as InfoIcon,
    Save as SaveIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

function TvPackageRecommender() {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [channels, setChannels] = useState([]);
    const [pricing, setPricing] = useState({});
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [recommendation, setRecommendation] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [saveDialog, setSaveDialog] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [channelsRes, pricingRes] = await Promise.all([
                axios.get('/api/tv-packages/channels'),
                axios.get('/api/tv-packages/pricing')
            ]);
            
            setChannels(channelsRes.data.channels || []);
            setPricing(pricingRes.data || {});
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setLoading(false);
    };

    const handleCalculateRecommendation = async () => {
        if (selectedChannels.length === 0) {
            return;
        }

        setCalculating(true);
        try {
            const response = await axios.post('/api/tv-packages/recommend', {
                selectedChannels
            });
            setRecommendation(response.data.recommendation);
        } catch (error) {
            console.error('Error calculating recommendation:', error);
        }
        setCalculating(false);
    };

    const handleSaveQuote = async () => {
        if (!customerInfo.name || !customerInfo.email) {
            return;
        }

        setSaving(true);
        try {
            const quoteData = {
                customerName: customerInfo.name,
                customerEmail: customerInfo.email,
                selectedChannels: selectedChannels.map(ch => ch.name),
                recommendedPackage: recommendation.bestValue?.pkgStr || 'Core',
                basePrice: recommendation.bestValue?.basePrice || 0,
                addonPrice: recommendation.bestValue?.addOnTotal || 0,
                totalPrice: recommendation.bestValue?.total || 0,
                themePacks: recommendation.requiredThemePacks || [],
                premiumChannels: recommendation.premiumChannels || []
            };

            await axios.post('/api/tv-packages/save-quote', quoteData);
            setSaveDialog(false);
            setCustomerInfo({ name: '', email: '' });
            // Could add success notification here
        } catch (error) {
            console.error('Error saving quote:', error);
        }
        setSaving(false);
    };

    const formatPrice = (price) => {
        return `$${parseFloat(price).toFixed(2)}/month`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LiveTvIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                TV Package Calculator
            </Typography>

            {/* Search and Selection */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Select Channels or Theme Packs
                </Typography>
                
                <Autocomplete
                    multiple
                    options={channels}
                    value={selectedChannels}
                    onChange={(event, newValue) => {
                        // Check for conflicts
                        const lastAdded = newValue[newValue.length - 1];
                        if (lastAdded) {
                            // If adding a theme pack, check for individual channels
                            if (lastAdded.type === 'theme-pack') {
                                const conflictingChannels = selectedChannels.filter(
                                    ch => ch.type === 'theme' && 
                                    lastAdded.channels?.some(packCh => 
                                        packCh.toLowerCase() === ch.name.toLowerCase()
                                    )
                                );
                                if (conflictingChannels.length > 0) {
                                    alert(`Cannot add this theme pack. You've already selected individual channels from it: ${conflictingChannels.map(ch => ch.name).join(', ')}`);
                                    return;
                                }
                            }
                            // If adding an individual channel, check for theme packs
                            if (lastAdded.type === 'theme') {
                                const conflictingPacks = selectedChannels.filter(
                                    ch => ch.type === 'theme-pack' && 
                                    ch.channels?.some(packCh => 
                                        packCh.toLowerCase() === lastAdded.name.toLowerCase()
                                    )
                                );
                                if (conflictingPacks.length > 0) {
                                    alert(`This channel is already included in the selected theme pack: ${conflictingPacks[0].name}`);
                                    return;
                                }
                            }
                        }
                        setSelectedChannels(newValue);
                        setRecommendation(null); // Clear previous recommendation
                    }}
                    getOptionLabel={(option) => option.name}
                    groupBy={(option) => {
                        if (option.type === 'theme-pack') return 'Theme Packs';
                        if (option.type === 'premium') return 'Premium Channels';
                        if (option.type === 'core') return 'Core Channels';
                        return 'Theme Pack Channels';
                    }}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body1">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.type === 'theme-pack' ? 'Theme Pack' : option.type}
                                    {option.packs && option.packs.length > 0 && ` | ${option.packs.join(', ')}`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                color={
                                    option.type === 'premium' ? 'secondary' :
                                    option.type === 'theme-pack' ? 'primary' : 'default'
                                }
                                size="small"
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search for channels or theme packs..."
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={{ mb: 3 }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleCalculateRecommendation}
                    disabled={selectedChannels.length === 0 || calculating}
                    startIcon={calculating ? <CircularProgress size={20} /> : <MoneyIcon />}
                >
                    {calculating ? 'Calculating...' : 'Get Recommendation'}
                </Button>
            </Paper>

            {/* Recommendation Results */}
            {recommendation && (
                <Grid container spacing={3}>
                    {/* Summary */}
                    {(recommendation?.requiredThemePacks?.length > 0 || recommendation?.premiumChannels?.length > 0) && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Selection Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {recommendation?.requiredThemePacks?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Required Theme Packs ({recommendation?.requiredThemePacks?.length || 0})
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {(recommendation?.requiredThemePacks || []).map((pack, idx) => (
                                                        <Chip key={idx} label={pack} size="small" color="primary" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                        {recommendation?.premiumChannels?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Premium Channels ({recommendation?.premiumChannels?.length || 0})
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {(recommendation?.premiumChannels || []).map((channel, idx) => (
                                                        <Chip key={idx} label={channel} size="small" color="secondary" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Best Value Option */}
                    {recommendation.bestValue && (
                        <Grid item xs={12} md={recommendation.alternative || recommendation.upsell ? 6 : 12}>
                            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'success.main' }}>
                                <CardContent>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CheckIcon sx={{ mr: 1 }} />
                                            Best Value Package
                                        </AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom>
                                        {recommendation.bestValue.pkgStr}
                                    </Typography>
                                    
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Base Price"
                                                secondary={formatPrice(recommendation.bestValue.basePrice)}
                                            />
                                        </ListItem>
                                        
                                        {recommendation.bestValue.addOnCount > 0 && (
                                            <ListItem>
                                                <ListItemText 
                                                    primary={`Add-on: ${recommendation.bestValue.addOnCount} extra theme pack${recommendation.bestValue.addOnCount > 1 ? 's' : ''}`}
                                                    secondary={`+ ${formatPrice(recommendation.bestValue.addOnTotal)}`}
                                                />
                                            </ListItem>
                                        )}
                                        
                                        <Divider />
                                        
                                        <ListItem>
                                            <ListItemText 
                                                primary={<Typography variant="h6">Total Price</Typography>}
                                                secondary={
                                                    <Typography variant="h5" color="primary">
                                                        {formatPrice(recommendation.bestValue.total)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                    
                                    {recommendation.bestValue.note && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {recommendation.bestValue.note}
                                        </Alert>
                                    )}
                                    
                                    {(recommendation.bestValue.extraThemes > 0 || recommendation.bestValue.extraPremiums > 0) && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            <AlertTitle>Bonus Included</AlertTitle>
                                            {recommendation.bestValue.extraThemes > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.bestValue.extraThemes} extra theme pack{recommendation.bestValue.extraThemes > 1 ? 's' : ''} of your choice
                                                </Typography>
                                            )}
                                            {recommendation.bestValue.extraPremiums > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.bestValue.extraPremiums} extra premium channel{recommendation.bestValue.extraPremiums > 1 ? 's' : ''} of your choice
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Required Package - Only show when backend provides it */}
                    {recommendation && recommendation.requiredPackage && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <AlertTitle>Required Package</AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom>
                                        {recommendation.requiredPackage.pkgStr}
                                    </Typography>
                                    
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Price"
                                                secondary={
                                                    <Typography variant="h6" color="warning.main">
                                                        {formatPrice(recommendation.requiredPackage.price)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                    
                                    {recommendation.requiredPackage.note && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {recommendation.requiredPackage.note}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Alternative Option */}
                    {recommendation.alternative && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <AlertTitle>Alternative Package</AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom>
                                        {recommendation.alternative.pkgStr}
                                    </Typography>
                                    
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Base Price"
                                                secondary={formatPrice(recommendation.alternative.basePrice)}
                                            />
                                        </ListItem>
                                        
                                        {recommendation.alternative.addOnCount > 0 && (
                                            <ListItem>
                                                <ListItemText 
                                                    primary={`Add-on: ${recommendation.alternative.addOnCount} extra theme pack${recommendation.alternative.addOnCount > 1 ? 's' : ''}`}
                                                    secondary={`+ ${formatPrice(recommendation.alternative.addOnTotal)}`}
                                                />
                                            </ListItem>
                                        )}
                                        
                                        <Divider />
                                        
                                        <ListItem>
                                            <ListItemText 
                                                primary={<Typography variant="h6">Total Price</Typography>}
                                                secondary={
                                                    <Typography variant="h5" color="primary">
                                                        {formatPrice(recommendation.alternative.total)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                    
                                    {recommendation.alternative.note && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {recommendation.alternative.note}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Upsell Option */}
                    {recommendation.upsell && recommendation.upsell.priceDiff > 0 && (
                        <Grid item xs={12}>
                            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <CardContent>
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mb: 2,
                                            backgroundColor: 'rgba(255,255,255,0.9)'
                                        }}
                                    >
                                        <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TrendingUpIcon sx={{ mr: 1 }} />
                                            Upgrade Opportunity
                                        </AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
                                        {recommendation.upsell.pkgStr}
                                    </Typography>
                                    
                                    <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                                        {formatPrice(recommendation.upsell.price)}
                                    </Typography>
                                    
                                    <Alert severity="success" sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                                        <Typography variant="body1">
                                            For just <strong>{formatPrice(recommendation.upsell.priceDiff || 0)}</strong> more, you get:
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {recommendation.upsell.extraThemes > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.upsell.extraThemes} extra theme pack{recommendation.upsell.extraThemes > 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            {recommendation.upsell.extraPremiums > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.upsell.extraPremiums} extra premium channel{recommendation.upsell.extraPremiums > 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            {(!recommendation.upsell.extraThemes || recommendation.upsell.extraThemes === 0) && 
                                             (!recommendation.upsell.extraPremiums || recommendation.upsell.extraPremiums === 0) && (
                                                <Typography variant="body2">
                                                    • Better value for your selection
                                                </Typography>
                                            )}
                                        </Box>
                                    </Alert>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Action Buttons - Hidden for now, may be enabled in future */}
                    {/* <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={() => setSaveDialog(true)}
                            >
                                Save Quote
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<EmailIcon />}
                                onClick={() => setSaveDialog(true)}
                            >
                                Email to Customer
                            </Button>
                        </Box>
                    </Grid> */}
                </Grid>
            )}

            {/* Save Quote Dialog */}
            <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Save Quote</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Customer Name"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Customer Email"
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSaveQuote} 
                        variant="contained" 
                        disabled={saving || !customerInfo.name || !customerInfo.email}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TvPackageRecommender;